type PdfRuntime = {
  getDocument: (source: { data: Uint8Array }) => {
    promise: Promise<{
      getPage: (pageNumber: number) => Promise<{
        getViewport: (options: { scale: number }) => { width: number; height: number };
        render: (options: { canvasContext: CanvasRenderingContext2D; viewport: { width: number; height: number } }) => { promise: Promise<void> };
      }>;
      destroy: () => Promise<void>;
    }>;
  };
};

function unwrapPdfRuntime(imported: unknown): PdfRuntime {
  const first = (imported as { default?: unknown }).default ?? imported;
  const second = (first as { default?: unknown }).default ?? first;
  return second as PdfRuntime;
}

/** Best-effort first-page poster from a PDF. Returns null if rendering isn't possible. */
export async function extractPdfPoster(source: File | ArrayBuffer): Promise<Blob | null> {
  try {
    await import('pdfjs-dist/legacy/build/pdf.worker.entry');
    const pdfjsLib = unwrapPdfRuntime(await import('pdfjs-dist/legacy/build/pdf'));
    const buffer = source instanceof File ? await source.arrayBuffer() : source;
    const data = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const doc = await pdfjsLib.getDocument({ data }).promise;
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(2.5, 1400 / Math.max(base.width, 1));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      await doc.destroy();
      return null;
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    await doc.destroy();
    return await new Promise((resolve) =>
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9)
    );
  } catch (error) {
    console.warn('Could not render PDF first page', error);
    return null;
  }
}
